/**
 * GHL submit action — published survey document only.
 *
 * Real top-level leave of Harry iff actionType is `"1"` and the URL is a
 * non-empty http(s) value. ActionType `"2"` (Message) does not take `_top`.
 * When to load in-frame: the *this-submit* thank-you is the calendar
 * ending ("Loading the calendar") via postMessage or a readable iframe
 * body — not every sticky submit. Guide / slots-filled copy stays put.
 * Where to load: `formAction.redirectUrl` when actionType is Message (`"2"`)
 * and the URL is non-empty http(s) (ADR-0011). Income is not the gate.
 *
 * Sticky postMessage payloads and Harry's hardcoded booking URL are not
 * inputs for the gate — sticky is a submit signal only.
 */

export const GHL_ACTION_REDIRECT = '1';
export const GHL_ACTION_MESSAGE = '2';

const CALENDAR_BOOKING_URL =
  'https://api.leadconnectorhq.com/widget/bookings/posture-assessment-walkthrough-call';

/** Calendar marker in GHL Message thank-you HTML (ellipsis-insensitive). */
export const GHL_CALENDAR_THANK_YOU_MARKER = 'Loading the calendar';

/** Conditional Message endings that must keep the GHL thank-you in the iframe. */
export const GHL_STAY_IN_IFRAME_THANK_YOU_MARKERS = [
  'free guide',
  'slots are filled',
  'calendar slots are filled',
  'self-paced path',
  'hybrid path',
  '1-on-1 coaching is the right fit',
  'download your posture report',
] as const;

export type GhlThankYouKind = 'calendar' | 'stay' | 'unknown';

/**
 * Published survey LqlEDJaFbS6i17cfJHfy: Message + calendar URL.
 * Conditional endings stay in the iframe. The default thank-you still
 * asks GHL to load the calendar (ADR-0011).
 */
export const PUBLISHED_SIMPLE_SURVEY_FORM = {
  formAction: {
    actionType: GHL_ACTION_MESSAGE,
    redirectUrl: CALENDAR_BOOKING_URL,
  },
  thankyouText:
    'Thanks! Loading the calendar… if it does not load automatically, click here.',
} as const;

export function isNonEmptyHttpUrl(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Accept a form object or a document `{ form }`. Reject arrays (sticky
 * postMessage) and strings (booking URLs) so those cannot be the gate.
 */
function unwrapPublishedForm(input: unknown): Record<string, unknown> | null {
  if (typeof input === 'string' || Array.isArray(input) || !isPlainObject(input)) {
    return null;
  }
  if (isPlainObject(input.formAction) || 'endsurveyType' in input || 'redirectUrl' in input) {
    return input;
  }
  if (isPlainObject(input.form)) {
    return input.form;
  }
  return input;
}

function ownString(record: Record<string, unknown>, key: string): string {
  if (!Object.prototype.hasOwnProperty.call(record, key)) return '';
  const value = record[key];
  return typeof value === 'string' ? value : '';
}

function resolveSubmitRedirectUrl(form: Record<string, unknown>): string {
  const formAction = isPlainObject(form.formAction) ? form.formAction : null;
  const actionUrl = formAction && typeof formAction.redirectUrl === 'string'
    ? formAction.redirectUrl
    : '';
  if (actionUrl.trim()) return actionUrl;
  // Form-root fallback only — never walk nested formAction / other objects.
  return ownString(form, 'redirectUrl');
}

function isRealRedirectPair(actionType: unknown, url: unknown): boolean {
  return actionType === GHL_ACTION_REDIRECT && isNonEmptyHttpUrl(url);
}

/** True when the published submit action will navigate `window.top`. */
export function isRealGhlTopRedirect(input: unknown): boolean {
  const form = unwrapPublishedForm(input);
  if (!form) return false;
  const formAction = isPlainObject(form.formAction) ? form.formAction : null;
  const actionType = formAction?.actionType;
  return isRealRedirectPair(actionType, resolveSubmitRedirectUrl(form));
}

/** True when disqualify is a real top redirect (`endsurveyType` + `endsurveyUrl`). */
export function isRealGhlDisqualifyRedirect(input: unknown): boolean {
  const form = unwrapPublishedForm(input);
  if (!form) return false;
  return isRealRedirectPair(form.endsurveyType, form.endsurveyUrl);
}

/** Submit or disqualify will take the Harry tab. */
export function willGhlSubmitTakeHarryTab(input: unknown): boolean {
  return isRealGhlTopRedirect(input) || isRealGhlDisqualifyRedirect(input);
}

export function includesGhlCalendarThankYou(text: string): boolean {
  return classifyGhlThankYouText(text) === 'calendar';
}

export function classifyGhlThankYouText(text: string): GhlThankYouKind {
  if (!text) return 'unknown';
  const normalized = text.replace(/\u2026/g, '...').toLowerCase();
  if (GHL_STAY_IN_IFRAME_THANK_YOU_MARKERS.some((marker) => normalized.includes(marker))) {
    return 'stay';
  }
  if (normalized.includes(GHL_CALENDAR_THANK_YOU_MARKER.toLowerCase())) {
    return 'calendar';
  }
  return 'unknown';
}

export function classifyGhlThankYouPayload(data: unknown): GhlThankYouKind {
  if (!data) return 'unknown';
  try {
    const text = typeof data === 'string' ? data : JSON.stringify(data);
    return classifyGhlThankYouText(text);
  } catch {
    return 'unknown';
  }
}

export function resolvePublishedRedirectUrl(
  input: unknown = PUBLISHED_SIMPLE_SURVEY_FORM,
): string {
  const form = unwrapPublishedForm(input);
  if (!form) return '';
  const url = resolveSubmitRedirectUrl(form);
  return isNonEmptyHttpUrl(url) ? url.trim() : '';
}

/** @deprecated Use resolvePublishedRedirectUrl — destination is the form URL. */
export const resolvePublishedCalendarUrl = resolvePublishedRedirectUrl;

/**
 * In-frame destination after submit. Empty when GHL takes `_top` or the
 * published form has no http(s) redirectUrl. Sticky JSON is not an input.
 */
export function resolveInFrameRedirectUrl(
  input: unknown = PUBLISHED_SIMPLE_SURVEY_FORM,
): string {
  if (isRealGhlTopRedirect(input)) return '';
  return resolvePublishedRedirectUrl(input);
}

/** True when Message + http(s) redirectUrl — Harry should load that URL in-frame. */
export function publishedThankYouWantsCalendar(
  input: unknown = PUBLISHED_SIMPLE_SURVEY_FORM,
): boolean {
  return Boolean(resolveInFrameRedirectUrl(input));
}
